// Carrom board — interactive prototype with planck.js physics.
//
// ─── Why setPointerCapture, not window-level listeners ──────────────
//
// Earlier versions used `window.addEventListener('pointerup', ...)`
// to catch the release of a slingshot drag. That pattern relies on
// the browser bubbling the pointerup event from the original target
// up through the DOM to window. In practice — especially on touch
// devices — this is NOT reliable:
//
//   • The browser sometimes interprets a fast/wide drag as a scroll
//     gesture and fires `pointercancel` instead of `pointerup`.
//   • On some Android Chrome versions, pointer events are sent to a
//     different document context after capture is implicitly broken
//     by a parent layer.
//   • A late `pointerup` can race a re-render and arrive before the
//     listener is technically attached if the listener was added in
//     a useEffect (already fixed) — but capture also helps belt-and-
//     suspenders here.
//
// setPointerCapture(pointerId) on the element being interacted with
// EXPLICITLY routes every subsequent event for that pointer ID to
// that element until release. Combined with treating pointercancel
// and lostpointercapture identically to pointerup, the gesture
// cannot be lost mid-flight.
//
// ─── Console diagnostics ───────────────────────────────────────────
//
// In the browser DevTools console, run:
//     window.__CARROM_DEBUG__ = true
// to enable per-event logging. Useful when the gesture still
// misbehaves on a specific device — the log line that's missing
// tells us exactly which event the browser isn't delivering.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  simulateShot,
  type SimCoin,
  type SimStriker,
  type ShotResult,
  COIN_R,
  STRIKER_R,
  FRAME_M,
  WALL_M,
  POCKET_FALL_RADIUS,
  SINK_DOTS,
} from './physics';
import { playCollision, playPocketDrop } from './sounds';

// Master switch for the temporary visual debug overlays:
//   • red dashed circles + yellow dots — pocket fall-radius
//   • cyan dashed rectangle — playable wall rectangle
//   • green dashed rings + green dots — sink-display dots
//   • orange dashed circle — striker hit zone
// Flipping this single constant hides all of them at once. The
// underlying gameplay (sink-snap to dot, oversized hit zone,
// etc.) keeps working — only the visualisations disappear.
const SHOW_POCKET_DEBUG = false;

export type Coin = SimCoin;
export type Striker = SimStriker;
export type Seat = 'p1' | 'p2' | 'p3' | 'p4';

type Props = {
  coins: Coin[];
  striker: Striker;
  canShoot: boolean;
  strikerRange?: [number, number];
  onShotResult: (result: ShotResult) => void;
  onStrikerMove: (x: number) => void;
  yourSeat: Seat;
  /** When non-null, coins of this colour pulse with a shine
   *  animation — used to show whose turn it is on the local
   *  player's device. Pass `null` to suppress (e.g. when it's
   *  the opponent's turn the local player should NOT see their
   *  own coins shining). */
  shineColor?: 'white' | 'black' | null;
  /** Fired the instant the user releases the slingshot — BEFORE
   *  the local replay runs. Page.tsx forwards the angle/power/
   *  strikerX to the server via the WS so the authoritative
   *  resolution is computed there; the local sim is treated as
   *  a preview that the server's `carrom_shot_resolved` overrides
   *  on arrival. Optional — when omitted the Board behaves
   *  exactly like the offline mock build. */
  onShotFired?: (shot: { angle: number; power: number; strikerX: number }) => void;
  /** When true, the entire board is visually rotated 180° so a
   *  player on the opposite seat sees THEIR own striker at the
   *  visual bottom of the screen (and the opponent's at the
   *  visual top). Pointer-event coordinates are also flipped
   *  internally so the slingshot drag still feels natural — the
   *  user pulls back away from their striker exactly as the
   *  un-flipped seat does. */
  viewFlip?: boolean;
  /** Live aim preview from the OPPONENT, throttled and relayed
   *  via the server while they drag their slingshot. Rendered as
   *  a subtler, dashed aim arrow originating from their striker
   *  position so you can see exactly where they're about to fire.
   *  `null` = opponent is not aiming (or just released). */
  opponentAim?: { angle: number; power: number; strikerX?: number | null } | null;
  /** Throttled aim broadcaster — fired roughly every 50 ms while
   *  the LOCAL user is dragging the slingshot, plus once with
   *  `null` when they cancel without firing. Page.tsx forwards to
   *  the WS so the other player sees the live preview. */
  onAimUpdate?: (aim: { angle: number; power: number; strikerX: number } | null) => void;
  /** True while the opponent's shot is being replayed via the
   *  parent's rAF loop — striker.x is animating then, so the
   *  slider thumb must stop tracking it (it'd otherwise gallop
   *  along with the in-flight puck). The thumb sits at the
   *  centre during a replay and snaps to the canonical striker
   *  the moment the replay completes. */
  replayActive?: boolean;
  /** When true, render the striker in the inverse colour scheme
   *  (blue body, yellow flower) so it visually reads as the
   *  OPPONENT's puck. Drives the `.opponent` class on the striker
   *  div. The local user's own striker keeps the canonical
   *  yellow body / blue flower scheme. Page.tsx flips this on
   *  whenever `currentTurn !== yourSeat`, including during the
   *  rAF replay of the opponent's shot. */
  strikerIsOpponent?: boolean;
};

const MAX_AIM_VISUAL = 0.55;
const MIN_FIRE_POWER = 0.025;
const POWER_FULL_DRAG = 0.30;
export const BASELINE_Y = 0.86;

// ─── Aim-arrow dot tunables ─────────────────────────────────────
// The forward aim is rendered as a row of N dots that taper in
// both size and opacity from the striker outward. At low power
// the dots are squeezed close together; at high power they spread
// out — so the gap between dots is itself a visual reading of
// power, alongside the green→yellow→red colour scale.
const NUM_AIM_DOTS = 8;
const AIM_DOT_T_START = 0.10;  // first dot starts 10% of the way from striker, so it doesn't overlap the puck
const AIM_DOT_T_END   = 1.00;  // last dot at the aim end (= aimEnd)
const AIM_DOT_R_MAX   = 1.40;  // largest dot radius (in viewBox units, 0–100)
const AIM_DOT_R_MIN   = 0.50;  // smallest dot radius (final dot)
const AIM_DOT_OP_MAX  = 0.95;
const AIM_DOT_OP_MIN  = 0.20;

// Striker x-range = the band where the striker can be repositioned
// along the bottom baseline. The inset (0.10) is tuned so the
// slider's full travel maps to the full visible baseline drawn in
// Board.png (between the two red endpoint dots). Reducing the
// inset further moves the striker closer to the corner pockets;
// increasing it pulls the striker inward.
const DEFAULT_RANGE: [number, number] = [
  FRAME_M + STRIKER_R + 0.12,
  1 - FRAME_M - STRIKER_R - 0.12,
];

type ActiveGesture = {
  kind: 'aim' | 'pos';
  pointerId: number;
  cleanup: () => void;
};

type AimData = {
  anchor:  { x: number; y: number };
  pointer: { x: number; y: number };
};

// Tiny wrapper so calls compile out cleanly when debug is off, and
// noisy log lines never leak to production users by default.
function dbg(...args: unknown[]) {
  if (typeof window !== 'undefined' && (window as unknown as { __CARROM_DEBUG__?: boolean }).__CARROM_DEBUG__) {
    // eslint-disable-next-line no-console
    console.log('[carrom]', ...args);
  }
}

export default function Board({
  coins,
  striker,
  canShoot,
  strikerRange = DEFAULT_RANGE,
  onShotResult,
  onStrikerMove,
  yourSeat,
  shineColor = null,
  onShotFired,
  viewFlip = false,
  opponentAim = null,
  onAimUpdate,
  replayActive = false,
  strikerIsOpponent = false,
}: Props) {
  const boardRef = useRef<HTMLDivElement | null>(null);

  const gestureRef = useRef<ActiveGesture | null>(null);
  const aimRef     = useRef<AimData       | null>(null);
  const [aimVersion, setAimVersion] = useState(0);
  const bumpAim = useCallback(() => setAimVersion((v) => (v + 1) & 0xffff), []);

  const [simCoins, setSimCoins]       = useState<Coin[]    | null>(null);
  const [simStriker, setSimStriker]   = useState<Striker  | null>(null);
  // Per-corner timestamp of the latest sink. React `key` re-mounts
  // the .carrom-pocket-flash element on every new sink so the CSS
  // ring animation restarts cleanly on consecutive sinks in the
  // same pocket. Cleared implicitly when the animation ends — the
  // overlay element stays in the DOM but its opacity returns to 0.
  const [pocketFlash, setPocketFlash] = useState<{tl?: number; tr?: number; bl?: number; br?: number}>({});
  const playingRef                    = useRef(false);
  const rafRef                        = useRef<number | null>(null);
  const cleanupTimeoutRef             = useRef<number | null>(null);
  const isSimulating                  = simCoins !== null;
  const [blockedFlash, setBlockedFlash] = useState(false);

  const toBoardCoords = useCallback((clientX: number, clientY: number) => {
    const el = boardRef.current;
    if (!el) return { x: 0, y: 0 };
    const rect = el.getBoundingClientRect();
    let x = (clientX - rect.left) / rect.width;
    let y = (clientY - rect.top)  / rect.height;
    // CSS rotate(180deg) on the wrapper visually flips everything but
    // pointer-event coords still arrive in screen space — what the
    // user touches on the visual bottom is internally near the top
    // of the unrotated coordinate system. Map back to canonical so
    // every downstream calculation (striker drag, aim angle, hit
    // tests) keeps working in a single coordinate frame.
    if (viewFlip) {
      x = 1 - x;
      y = 1 - y;
    }
    return { x, y };
  }, [viewFlip]);

  // Numeric seat for placement checks — `yourSeat` is a 'p1'..'p4'
  // string; canPlaceStrikerAt wants 1..4 so it can pick the right
  // baseline (white = 0.86, black = 0.14). Without this the slider
  // for a black-team player validates against y = 0.86 and gets
  // blocked by coins near the white baseline even though their
  // actual striker sits at y = 0.14.
  const seatNum = (yourSeat === 'p1' ? 1 : yourSeat === 'p2' ? 2 : yourSeat === 'p3' ? 3 : 4) as 1 | 2 | 3 | 4;

  const requestStrikerX = useCallback((targetX: number) => {
    const clamped = clamp(targetX, strikerRange[0], strikerRange[1]);
    if (canPlaceStrikerAt(clamped, coins, seatNum)) {
      onStrikerMove(clamped);
      return;
    }
    for (let d = 0.002; d <= 0.5; d += 0.002) {
      const xL = clamped - d;
      const xR = clamped + d;
      if (xL >= strikerRange[0] && canPlaceStrikerAt(xL, coins, seatNum)) { onStrikerMove(xL); return; }
      if (xR <= strikerRange[1] && canPlaceStrikerAt(xR, coins, seatNum)) { onStrikerMove(xR); return; }
    }
    setBlockedFlash(true);
    window.setTimeout(() => setBlockedFlash(false), 500);
  }, [coins, strikerRange, onStrikerMove, seatNum]);

  const endGesture = useCallback(() => {
    const g = gestureRef.current;
    if (g) {
      gestureRef.current = null;
      try { g.cleanup(); } catch { /* swallow */ }
    }
    if (aimRef.current) {
      aimRef.current = null;
      bumpAim();
    }
  }, [bumpAim]);

  // Page-level safety nets — clear any in-flight gesture if the tab
  // backgrounds, the window blurs, or the page hides.
  useEffect(() => {
    const onVis = () => { if (document.visibilityState !== 'visible') endGesture(); };
    const onBlur = () => endGesture();
    const onPageHide = () => endGesture();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('blur', onBlur);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [endGesture]);

  useEffect(() => () => {
    if (rafRef.current != null)            cancelAnimationFrame(rafRef.current);
    if (cleanupTimeoutRef.current != null) clearTimeout(cleanupTimeoutRef.current);
    playingRef.current = false;
    endGesture();
  }, [endGesture]);

  // Stuck-slider safety net — if the player loses the turn mid-drag
  // (e.g. timeout pass while their finger is still on the slider),
  // or a shot starts simulating, force-clean any active gesture so
  // gestureRef.current is empty when the turn comes back. Without
  // this, the next pointerdown bails on the line
  // `if (gestureRef.current) return;` guard inside startSliderDrag,
  // and the slider sits frozen until the page is refreshed — which
  // is exactly the user-reported "sometimes do not let me move the
  // striker by that X-axis controller until you refresh the page".
  // Tracked against `!canShoot || isSimulating` so the cleanup
  // fires the moment the slider is no longer interactive.
  useEffect(() => {
    if (!canShoot || isSimulating) {
      endGesture();
    }
  }, [canShoot, isSimulating, endGesture]);

  const doFire = useCallback((angle: number, power: number) => {
    dbg('doFire', { angle: angle.toFixed(2), power: power.toFixed(2) });
    if (playingRef.current && simCoins !== null) { dbg('doFire EARLY-RETURN — already simulating'); return; }
    playingRef.current = true;

    // Send the shot to the server FIRST — synchronously, before the
    // local replay starts. The server's authoritative resolution
    // arrives during the ~0.5–2 s the local animation takes, and
    // Page.tsx applies the server's finalCoins / striker / scores
    // when both the broadcast and the animation have landed.
    if (onShotFired) {
      try { onShotFired({ angle, power, strikerX: striker.x }); }
      catch (e) { console.error('[carrom] onShotFired threw:', e); }
    }

    let result: ShotResult;
    try {
      result = simulateShot(coins, striker, angle, power);
    } catch (err) {
      // If planck ever throws, don't strand the game — log it,
      // reset the playing flag, and let the user try another shot.
      // eslint-disable-next-line no-console
      console.error('[carrom] simulateShot crashed:', err);
      playingRef.current = false;
      return;
    }
    dbg('doFire simulated', { frames: result.timeline.length, sunk: result.sunkCoinIds.length });
    const startedAt = performance.now();
    const STEP_MS = 1000 / 60;
    // Index into result.events — advances forward only as the
    // playback wall-clock catches up to each event's `t` value.
    let nextEventIdx = 0;

    const tick = () => {
      const elapsed = performance.now() - startedAt;
      const idx = Math.min(result.timeline.length - 1, Math.floor(elapsed / STEP_MS));
      const f = result.timeline[idx];
      setSimCoins(f.coins);
      setSimStriker(f.striker);

      // Fire any sound events whose simulated time ≤ playback time.
      // Only collision + non-striker sink sounds fire here. The
      // striker-sunk (foul) sound is played by the parent in
      // `onShotResult` so it lands together with the foul overlay.
      const elapsedSec = elapsed / 1000;
      while (
        nextEventIdx < result.events.length &&
        result.events[nextEventIdx].t <= elapsedSec
      ) {
        const ev = result.events[nextEventIdx];
        if (ev.type === 'collide-cc' || ev.type === 'collide-sc') {
          playCollision(ev.intensity);
        } else if (ev.type === 'sink') {
          playPocketDrop();
          const c = f.coins.find((co) => co.id === ev.coinId);
          if (c) {
            const corner = `${c.y < 0.5 ? 't' : 'b'}${c.x < 0.5 ? 'l' : 'r'}` as 'tl'|'tr'|'bl'|'br';
            setPocketFlash((p) => ({ ...p, [corner]: Date.now() }));
          }
        } else if (ev.type === 'striker-sunk') {
          const corner = `${f.striker.y < 0.5 ? 't' : 'b'}${f.striker.x < 0.5 ? 'l' : 'r'}` as 'tl'|'tr'|'bl'|'br';
          setPocketFlash((p) => ({ ...p, [corner]: Date.now() }));
        }
        nextEventIdx += 1;
      }

      if (idx >= result.timeline.length - 1) {
        playingRef.current = false;
        rafRef.current = null;
        cleanupTimeoutRef.current = window.setTimeout(() => {
          cleanupTimeoutRef.current = null;
          setSimCoins(null);
          setSimStriker(null);
          playingRef.current = false;
          endGesture();
          onShotResult(result);
        }, result.strikerSunk ? 1700 : 30);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [coins, striker, simCoins, endGesture, onShotResult, onShotFired]);

  // ─── Aim drag (slingshot on the striker) — uses setPointerCapture
  const startAimDrag = useCallback((ev: React.PointerEvent) => {
    dbg('startAimDrag', { canShoot, isSimulating, pointerId: ev.pointerId, type: ev.pointerType });
    if (!canShoot || isSimulating) { dbg('startAimDrag BLOCKED'); return; }
    // Same robustness fix as startSliderDrag: NEVER bail because
    // gestureRef is still populated from a stuck previous gesture.
    // Force-clean and start fresh — that's what un-sticks the
    // "frozen until refresh" state.
    endGesture();
    gestureRef.current = null;
    ev.stopPropagation();

    const hitEl = ev.currentTarget as HTMLElement;
    const pointerId = ev.pointerId;
    const startPointer = toBoardCoords(ev.clientX, ev.clientY);

    aimRef.current = { anchor: { x: striker.x, y: striker.y }, pointer: startPointer };
    bumpAim();

    // Best-effort capture — see the matching comment in
    // startSliderDrag. The window-level listeners below are the
    // actual reliability layer; capture is just an optimisation.
    try { hitEl.setPointerCapture(pointerId); dbg('setPointerCapture OK', pointerId); }
    catch (e) { dbg('setPointerCapture FAILED', e); }

    let active = true;
    const onMove = (e: PointerEvent) => {
      if (!active || e.pointerId !== pointerId) return;
      if (!aimRef.current) return;
      aimRef.current = { ...aimRef.current, pointer: toBoardCoords(e.clientX, e.clientY) };
      bumpAim();
    };

    // pointerup AND pointercancel are both treated as "the user
    // finished" and fire the shot. lostpointercapture used to be in
    // here too — but with window-level listeners we don't care
    // about capture status; the user lifting their finger is what
    // matters. pointercancel does NOT throw away the user's intent
    // (long-standing fix preserved).
    const finish = (e: PointerEvent) => {
      if (!active || e.pointerId !== pointerId) return;
      dbg('finish via', e.type, { pointerId: e.pointerId });
      cleanup();
      const a = aimRef.current;
      aimRef.current = null;
      bumpAim();
      if (!a) return;
      const p = toBoardCoords(e.clientX, e.clientY);
      const dx = p.x - a.anchor.x;
      const dy = p.y - a.anchor.y;
      const dist = Math.hypot(dx, dy);
      const power = Math.min(1, dist / POWER_FULL_DRAG);
      dbg('release power', { dist: dist.toFixed(3), power: power.toFixed(3) });
      if (power < MIN_FIRE_POWER) { dbg('below MIN_FIRE_POWER — no shot'); return; }
      const angle = Math.atan2(-dy, -dx);
      doFire(angle, power);
    };

    function cleanup() {
      active = false;
      window.removeEventListener('pointermove',   onMove);
      window.removeEventListener('pointerup',     finish);
      window.removeEventListener('pointercancel', finish);
      try { hitEl.releasePointerCapture(pointerId); }
      catch { /* not held / detached */ }
      if (gestureRef.current && gestureRef.current.pointerId === pointerId) {
        gestureRef.current = null;
      }
    }

    // Window-level — survives any DOM re-render of the striker
    // hitzone or board container. setPointerCapture above is
    // best-effort; this is the reliability layer.
    window.addEventListener('pointermove',   onMove);
    window.addEventListener('pointerup',     finish);
    window.addEventListener('pointercancel', finish);

    gestureRef.current = { kind: 'aim', pointerId, cleanup };
    dbg('aim listeners attached');
  }, [canShoot, isSimulating, striker.x, striker.y, toBoardCoords, bumpAim, endGesture, doFire]);

  // ─── Slider drag — separate slider row BELOW the board ───────────
  // The slider lives outside the board entirely so it can never
  // collide with the striker drag. Pressing the slider track maps
  // the pointer's x within the slider rectangle to a striker x in
  // the [strikerRange[0], strikerRange[1]] band, then setPointerCapture
  // ensures dragging continues to work even when the finger leaves
  // the slider.
  const startSliderDrag = useCallback((ev: React.PointerEvent) => {
    if (!canShoot || isSimulating) return;
    // ALWAYS force-clean any existing gesture before starting a new
    // one. The previous version returned silently if gestureRef was
    // populated, which was the root cause of the user-reported
    // "slider stuck until refresh": when a turn changed mid-drag or
    // a Board re-render lost the captured target element, the
    // cleanup function held a closure to a now-detached DOM node,
    // releasePointerCapture became a no-op, gestureRef stayed
    // populated, and every subsequent pointerdown bailed at this
    // guard. The fix is to NEVER bail — kill whatever gesture is
    // there, then start fresh.
    endGesture();
    gestureRef.current = null;
    ev.stopPropagation();

    const railEl = ev.currentTarget as HTMLElement;
    const pointerId = ev.pointerId;

    // Capture the rail's bounding rect AT POINTERDOWN TIME and use
    // it for the whole gesture. Re-fetching getBoundingClientRect
    // on every move would normally be fine, but if React re-renders
    // mid-drag (because canShoot changes, isSimulating toggles, or
    // a striker_move broadcast updates state) the rail element can
    // be replaced — `railEl.getBoundingClientRect()` then refers
    // to a detached element and returns all-zeros, mapping every
    // finger position to t=0. Pinning the rect at gesture start
    // eliminates that whole class of "the slider stops working
    // mid-drag" bug.
    const railRect = railEl.getBoundingClientRect();

    const sliderXFromEv = (clientX: number): number => {
      let t = clamp((clientX - railRect.left) / railRect.width, 0, 1);
      // P2 / black-team view rotates the wrapper 180°, so visual-left
      // maps to canonical-right. Flip `t` to match — exactly what
      // toBoardCoords does for the aim drag.
      if (viewFlip) t = 1 - t;
      return strikerRange[0] + t * (strikerRange[1] - strikerRange[0]);
    };

    requestStrikerX(sliderXFromEv(ev.clientX));

    // Listen at the WINDOW level instead of on the rail element.
    // setPointerCapture worked when the rail stayed mounted, but it
    // is unreliable on touchscreens AND it pegs all events to the
    // capturing element — so if React unmounts that element (any
    // re-render that re-keys the rail), the capture transfers to
    // a detached node and pointerup never reaches our cleanup. The
    // user's finger lifts and we never know, so gestureRef stays
    // dirty. Window-level listeners survive any DOM churn.
    let active = true;
    const onMove = (e: PointerEvent) => {
      if (!active || e.pointerId !== pointerId) return;
      requestStrikerX(sliderXFromEv(e.clientX));
    };
    const finish = (e: PointerEvent) => {
      if (!active || e.pointerId !== pointerId) return;
      cleanup();
    };
    function cleanup() {
      active = false;
      window.removeEventListener('pointermove',   onMove);
      window.removeEventListener('pointerup',     finish);
      window.removeEventListener('pointercancel', finish);
      // Best-effort: if the rail element is still alive in the DOM,
      // release the pointer capture too. If it's detached (re-render
      // happened mid-drag), this throws silently and we don't care.
      try { railEl.releasePointerCapture(pointerId); }
      catch { /* not held / detached */ }
      if (gestureRef.current && gestureRef.current.pointerId === pointerId) {
        gestureRef.current = null;
      }
    }

    window.addEventListener('pointermove',   onMove);
    window.addEventListener('pointerup',     finish);
    window.addEventListener('pointercancel', finish);
    // Best-effort capture for browsers that respect it. Even when
    // capture is lost or transferred (any re-render), the
    // window-level listeners above keep the drag alive.
    try { railEl.setPointerCapture(pointerId); } catch { /* ok */ }

    gestureRef.current = { kind: 'pos', pointerId, cleanup };
  }, [canShoot, isSimulating, strikerRange, requestStrikerX, endGesture, viewFlip]);

  const handleStrikerPointerDown = startAimDrag;
  const handleSliderPointerDown  = startSliderDrag;

  const aim = useMemo(() => {
    void aimVersion;
    const a = aimRef.current;
    if (!a) return null;
    const dx = a.pointer.x - a.anchor.x;
    const dy = a.pointer.y - a.anchor.y;
    const dist = Math.hypot(dx, dy);
    const power = Math.min(1, dist / POWER_FULL_DRAG);
    const angle = Math.atan2(-dy, -dx);
    return { angle, power, dragPointer: a.pointer };
  }, [aimVersion]);

  const renderCoins   = simCoins   ?? coins;
  const renderStriker = simStriker ?? striker;
  const aimEnd = aim
    ? {
        x: renderStriker.x + Math.cos(aim.angle) * MAX_AIM_VISUAL * aim.power,
        y: renderStriker.y + Math.sin(aim.angle) * MAX_AIM_VISUAL * aim.power,
      }
    : null;
  const isPosDragging = gestureRef.current?.kind === 'pos';
  const isAimDragging = aim !== null;

  // Opponent's aim — server-relayed broadcast of their slingshot
  // pull. Rendered with a slightly more transparent, dashed dot
  // strip so it visually reads as "their aim", not yours.
  //
  // The arrow is suppressed when power < ~0.04 so the bot's
  // GROW-FROM-ZERO frames (where power scales 0 → initial) don't
  // render 8 dots stacked exactly on the striker. With this
  // threshold the arrow first becomes visible once it's long
  // enough to actually point somewhere meaningful, then visibly
  // extends to its initial wobble length.
  const opponentAimRendered = opponentAim && opponentAim.power >= 0.04
    ? opponentAim
    : null;
  const opponentAimEnd = opponentAimRendered
    ? {
        x: renderStriker.x + Math.cos(opponentAimRendered.angle) * MAX_AIM_VISUAL * opponentAimRendered.power,
        y: renderStriker.y + Math.sin(opponentAimRendered.angle) * MAX_AIM_VISUAL * opponentAimRendered.power,
      }
    : null;

  // Throttled aim broadcaster — fires roughly every 50 ms while
  // the user drags, plus once with `null` on drag cancel so the
  // remote side hides the indicator. The shot itself implicitly
  // clears the aim (the server's `carrom_shot_resolved` triggers
  // a clear on the receiver), so we don't need to send a final
  // explicit clear when a shot lands.
  const lastAimSentAtRef = useRef(0);
  const lastAimSentNullRef = useRef(true);
  useEffect(() => {
    if (!onAimUpdate) return;
    if (aim === null) {
      // Drag ended without firing (cancel) — send a single clear if
      // we'd previously sent a non-null aim.
      if (!lastAimSentNullRef.current) {
        onAimUpdate(null);
        lastAimSentNullRef.current = true;
      }
      return;
    }
    const now = performance.now();
    if (now - lastAimSentAtRef.current < 50) return;
    lastAimSentAtRef.current = now;
    lastAimSentNullRef.current = false;
    onAimUpdate({ angle: aim.angle, power: aim.power, strikerX: striker.x });
  }, [aim, onAimUpdate, striker.x]);

  return (
    <div className="carrom-board-wrap">
      <div
        ref={boardRef}
        className={`carrom-board ${blockedFlash ? 'blocked' : ''}`}
        role="application"
        aria-label="Carrom board"
        style={{
          touchAction: 'none',
          // Per-player 180° rotation. Black-team seats (P2, P4) see
          // the canonical layout flipped so their active striker —
          // canonically at y=0.14 — visually sits on the visible
          // bottom of THEIR screen, with the opponent at the top.
          // Pointer coords are flipped in `toBoardCoords` to match.
          transform: viewFlip ? 'rotate(180deg)' : undefined,
        }}
      >
        <img
          className="carrom-board-bg"
          src="/games/carrom/Board.png"
          alt=""
          aria-hidden="true"
          draggable={false}
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
        />

        <div className="carrom-felt" />
        <div className="carrom-baseline-strip bottom" />
        <div className="carrom-baseline-strip top" />
        <div className="carrom-baseline-strip left" />
        <div className="carrom-baseline-strip right" />
        <div className="carrom-pocket-arc tl" />
        <div className="carrom-pocket-arc tr" />
        <div className="carrom-pocket-arc bl" />
        <div className="carrom-pocket-arc br" />
        <div className="carrom-center-circle" />
        <div className="carrom-center-cross" />
        <div className="carrom-pocket tl" />
        <div className="carrom-pocket tr" />
        <div className="carrom-pocket bl" />
        <div className="carrom-pocket br" />
        {pocketFlash.tl && <div key={`pf-tl-${pocketFlash.tl}`} className="carrom-pocket-flash tl" />}
        {pocketFlash.tr && <div key={`pf-tr-${pocketFlash.tr}`} className="carrom-pocket-flash tr" />}
        {pocketFlash.bl && <div key={`pf-bl-${pocketFlash.bl}`} className="carrom-pocket-flash bl" />}
        {pocketFlash.br && <div key={`pf-br-${pocketFlash.br}`} className="carrom-pocket-flash br" />}

        {/* TEMP debug overlay — playfield rectangle (cyan dashed)
            shows where the physics walls actually are, i.e. how
            much "border" the wood frame is taking up. Compare
            this to the wood frame visible in the image; the
            felt should sit INSIDE the cyan rectangle. */}
        {SHOW_POCKET_DEBUG && (
          <div
            className="carrom-debug-walls"
            style={{
              left:   `${WALL_M * 100}%`,
              top:    `${WALL_M * 100}%`,
              right:  `${WALL_M * 100}%`,
              bottom: `${WALL_M * 100}%`,
            }}
          />
        )}

        {/* TEMP debug overlay — sink-display dots. The four dots
            mark the positions where a coin / striker visually
            ends up the moment it is sunk. They should align with
            the visible pocket holes drawn into the board image so
            sunk pieces appear inside those holes. Tunable via
            SINK_DOTS in physics.ts. Hidden when SHOW_POCKET_DEBUG
            is false; the snap-on-sink behaviour stays. */}
        {SHOW_POCKET_DEBUG && SINK_DOTS.map(([x, y], i) => (
          <div
            key={`debug-sink-${i}`}
            className="carrom-debug-sink-dot"
            style={{
              left: `${x * 100}%`,
              top:  `${y * 100}%`,
            }}
          />
        ))}

        {/* TEMP debug overlay — fall-radius circles on top of the
            board image so the user can see EXACTLY where physics
            sinks vs where the visible pocket on the image is.
            The dashed red circle is the fall zone (a coin's centre
            must enter this circle to be sunk). The yellow centre
            dot is the exact pocket centre used by physics. */}
        {SHOW_POCKET_DEBUG && [
          { cls: 'tl', x: FRAME_M,         y: FRAME_M },
          { cls: 'tr', x: 1 - FRAME_M,     y: FRAME_M },
          { cls: 'bl', x: FRAME_M,         y: 1 - FRAME_M },
          { cls: 'br', x: 1 - FRAME_M,     y: 1 - FRAME_M },
        ].map((p) => (
          <div
            key={`debug-pocket-${p.cls}`}
            className="carrom-debug-pocket"
            style={{
              left:   `${p.x * 100}%`,
              top:    `${p.y * 100}%`,
              width:  `${POCKET_FALL_RADIUS * 2 * 100}%`,
              height: `${POCKET_FALL_RADIUS * 2 * 100}%`,
            }}
          />
        ))}

        {renderCoins.map((c) => {
          // Coins of the active player's colour pulse with a shine
          // animation so the user can see at a glance which coins
          // are theirs to target. The shine is suppressed during
          // simulation playback (the result is already determined,
          // shining would be confusing while pieces fly).
          const shining = !!shineColor && shineColor === c.color && !c.sunk && !isSimulating;
          return (
            <div
              key={c.id}
              className={`carrom-coin ${c.color} ${c.sunk ? 'sunk' : ''} ${shining ? 'shining' : ''}`}
              style={{
                left:   `${c.x * 100}%`,
                top:    `${c.y * 100}%`,
                width:  `${COIN_R * 2 * 100}%`,
                height: `${COIN_R * 2 * 100}%`,
              }}
            />
          );
        })}

        {/* ─── Striker touch target (transparent, oversized) ────────
            This is an INVISIBLE hit zone, twice the diameter of the
            visible striker. It sits above the striker visual and is
            the element that receives `pointerdown` and gets the
            pointer-capture for the slingshot drag.

            Why a separate enlarged hit zone (do NOT remove this):
              1) On phones, the striker visual is small (~6% of
                 board width). Hitting that exactly with a finger is
                 fiddly and frustrating. With a 12% hit zone (2x
                 diameter, 4x area), a press anywhere reasonably
                 close to the striker still starts the gesture.
              2) The striker's PHYSICS body and VISUAL stay at
                 STRIKER_R = 0.030 — collisions with coins, the
                 walls, and the look of the puck are all unchanged.
                 Only the input affordance is enlarged.
              3) Decoupling the touch target from the rendered
                 sprite is the standard mobile-game pattern. If we
                 later swap the striker artwork, change the visual
                 size, or tweak physics, the touch target keeps
                 working at 2× — no special handling required.

            If a future contributor is tempted to fold this back
            into the visible striker: don't. The smaller visual is
            the correct physics+visual size; this hit zone is for
            humans. */}
        <div
          className={`carrom-striker-hitzone ${(!canShoot || isSimulating) ? 'disabled' : ''}`}
          style={{
            left:   `${renderStriker.x * 100}%`,
            top:    `${renderStriker.y * 100}%`,
            // 2× the striker DIAMETER means 4× the striker RADIUS
            // in the width/height calc (diameter = 2 * radius).
            width:  `${STRIKER_R * 4 * 100}%`,
            height: `${STRIKER_R * 4 * 100}%`,
            opacity: renderStriker.sunk ? 0 : 1,
          }}
          onPointerDown={handleStrikerPointerDown}
          aria-label="Striker — press and drag back like a slingshot"
          role="button"
        />

        {/* ─── Striker (visible + collision size) ──────────────────
            Pointer events are intentionally disabled here — the
            invisible hit zone above this element catches the press
            and sends it through `handleStrikerPointerDown`. The
            visible striker stays at the canonical STRIKER_R size
            so it lines up with the planck.js circle body 1:1. */}
        {/* The `sunk` class drives the carromCoinSink animation (CSS)
            so the striker visually shrinks + fades into the pocket
            the same way coins do, instead of just disappearing. The
            opacity inline-style is gone — the keyframes own opacity
            now.
            `disabled` is only applied when the striker belongs to
            the LOCAL player AND they can't currently shoot — that's
            the cursor-not-allowed + dimmed state for my-puck-but-
            opponent's-turn. The OPPONENT's striker (when it IS
            their turn but I can't shoot) must stay fully opaque,
            otherwise the whole "ghost-like blue puck" the user
            reported. */}
        <div
          className={`carrom-striker ${(!canShoot || isSimulating) && !strikerIsOpponent ? 'disabled' : ''} ${isAimDragging ? 'dragging' : ''} ${blockedFlash ? 'blocked' : ''} ${renderStriker.sunk ? 'sunk' : ''} ${strikerIsOpponent ? 'opponent' : ''}`}
          style={{
            left:   `${renderStriker.x * 100}%`,
            top:    `${renderStriker.y * 100}%`,
            width:  `${STRIKER_R * 2 * 100}%`,
            height: `${STRIKER_R * 2 * 100}%`,
          }}
          aria-hidden="true"
        />

        {/* TEMP striker hit-zone overlay — orange dashed ring at
            exactly the size of the (invisible) `.carrom-striker-
            hitzone` element above. Lets the user see how large
            the touch target actually is and decide whether to
            grow it. Hidden when SHOW_POCKET_DEBUG is false. */}
        {SHOW_POCKET_DEBUG && !renderStriker.sunk && (
          <div
            className="carrom-debug-striker-hitzone"
            style={{
              left:   `${renderStriker.x * 100}%`,
              top:    `${renderStriker.y * 100}%`,
              width:  `${STRIKER_R * 4 * 100}%`,
              height: `${STRIKER_R * 4 * 100}%`,
            }}
          />
        )}

        {aim && aimEnd && (
          <svg
            className="carrom-aim-overlay"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            {/* Backward "drag echo" — faint dashed line from the
                striker to the user's finger so the slingshot pull
                still reads visually. Forward aim is the dots. */}
            <line
              x1={renderStriker.x * 100}
              y1={renderStriker.y * 100}
              x2={Math.max(-10, Math.min(110, aim.dragPointer.x * 100))}
              y2={Math.max(-10, Math.min(110, aim.dragPointer.y * 100))}
              stroke="rgba(255,255,255,0.20)"
              strokeWidth={0.35}
              strokeDasharray="0.9 1.2"
            />

            {/* Forward aim — N tapered dots from striker to aimEnd.
                Size + opacity decrease toward the end. Distance
                between dots scales with line length, which scales
                with power, so the user reads power both from the
                colour AND from how spread out the dots are. */}
            {Array.from({ length: NUM_AIM_DOTS }, (_, i) => {
              const progress = i / (NUM_AIM_DOTS - 1);
              const t = AIM_DOT_T_START + (AIM_DOT_T_END - AIM_DOT_T_START) * progress;
              const cx = renderStriker.x * 100 + (aimEnd.x - renderStriker.x) * 100 * t;
              const cy = renderStriker.y * 100 + (aimEnd.y - renderStriker.y) * 100 * t;
              const r = AIM_DOT_R_MAX - (AIM_DOT_R_MAX - AIM_DOT_R_MIN) * progress;
              const op = AIM_DOT_OP_MAX - (AIM_DOT_OP_MAX - AIM_DOT_OP_MIN) * progress;
              return (
                <circle
                  key={i}
                  cx={cx}
                  cy={cy}
                  r={r}
                  fill={powerColor(aim.power)}
                  opacity={op}
                  style={{ filter: `drop-shadow(0 0 ${0.8 + aim.power * 1.4}px ${powerColor(aim.power, 0.55)})` }}
                />
              );
            })}
          </svg>
        )}

        {opponentAimRendered && opponentAimEnd && (
          <svg
            className="carrom-aim-overlay"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            {/* Opponent's live aim — same tapered-dot shape the
                local aim uses, but in white-translucent so the
                viewer can read at a glance "they're aiming" without
                confusing it with their own draft. The trail is
                rendered at ~60% opacity vs the local arrow's ~95%. */}
            {Array.from({ length: NUM_AIM_DOTS }, (_, i) => {
              const progress = i / (NUM_AIM_DOTS - 1);
              const t = AIM_DOT_T_START + (AIM_DOT_T_END - AIM_DOT_T_START) * progress;
              const cx = renderStriker.x * 100 + (opponentAimEnd.x - renderStriker.x) * 100 * t;
              const cy = renderStriker.y * 100 + (opponentAimEnd.y - renderStriker.y) * 100 * t;
              const r = AIM_DOT_R_MAX - (AIM_DOT_R_MAX - AIM_DOT_R_MIN) * progress;
              const op = (AIM_DOT_OP_MAX - (AIM_DOT_OP_MAX - AIM_DOT_OP_MIN) * progress) * 0.65;
              return (
                <circle
                  key={i}
                  cx={cx}
                  cy={cy}
                  r={r}
                  fill="rgba(255, 255, 255, 0.95)"
                  opacity={op}
                  stroke="rgba(0, 0, 0, 0.55)"
                  strokeWidth={0.18}
                />
              );
            })}
          </svg>
        )}
      </div>

      {/* Striker x-axis slider — lives BELOW the board so it can never
          interfere with the slingshot drag on the striker. The thumb
          mirrors striker.x WHEN IT'S YOUR TURN; on the opponent's
          turn we lock it to the centre so the user doesn't see their
          own slider thumb gliding around as the opponent drags or as
          the rAF replay walks the timeline. The drag itself is also
          gated by `canShoot` (see `startSliderDrag`), so the locked
          thumb is purely cosmetic — it can't be moved by touch when
          it's not your turn. */}
      <div
        className={`carrom-striker-rail ${isPosDragging ? 'active' : ''} ${(!canShoot || isSimulating) ? 'disabled' : ''}`}
        onPointerDown={handleSliderPointerDown}
        style={{ touchAction: 'none' }}
        role="slider"
        aria-label="Striker position"
        aria-valuemin={strikerRange[0]}
        aria-valuemax={strikerRange[1]}
        aria-valuenow={striker.x}
      >
        <div className="carrom-striker-rail-track" />
        <div
          className="carrom-striker-rail-thumb"
          style={{
            // The rail lives OUTSIDE the rotated board wrapper, so it
            // doesn't get the CSS 180° flip the board does. To keep
            // the thumb tracking the user's finger on P2's seat (and
            // matching the visual position of the canonical striker
            // on the rotated board), we explicitly mirror the
            // canonical t value when `viewFlip` is set. Without this
            // mirror, on P2's screen the thumb appears on the
            // opposite side of where the user dragged — the canonical
            // striker is correct (board rotation handles it), but the
            // rail's thumb flies the wrong way.
            //
            // Two gates also live here:
            //   • `!canShoot` (opponent's turn) → thumb locked to
            //     centre so it isn't a confusing distraction.
            //   • `replayActive` (rAF replay running on this device
            //     after our turn just flipped to true) → thumb stays
            //     at centre until the puck settles, then snaps to the
            //     canonical striker on the next render.
            left: (canShoot && !replayActive)
              ? (() => {
                  const t = (striker.x - strikerRange[0]) / (strikerRange[1] - strikerRange[0]);
                  const visT = viewFlip ? 1 - t : t;
                  return `${visT * 100}%`;
                })()
              : '50%',
          }}
        />
      </div>

      <span hidden aria-hidden data-seat={yourSeat} />
    </div>
  );
}

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v));
}

/** Baseline-aware striker placement check. Seats 1 + 3 (white team)
 *  shoot from y = 0.86 (canonical bottom). Seats 2 + 4 (black team)
 *  shoot from y = 0.14 (canonical top). The function checks whether
 *  a striker centred at (x, baselineY(seat)) clears every live coin
 *  by STRIKER_R + COIN_R.
 *
 *  Default seat is 1 (white) for backward compatibility with the
 *  pre-2P-seat-aware callers, but EVERY active call site should
 *  pass the actual seat — otherwise a black-team player's slider
 *  validates against the white-team baseline (y = 0.86) and gets
 *  spuriously blocked by any coin near the bottom of the board,
 *  even though the black striker actually sits at the TOP. That was
 *  the user-reported bug "if something is kept on my side of the
 *  start bar, it doesn't let the opponent move the slider" — the
 *  opponent's slider was checking against MY baseline.
 */
export function canPlaceStrikerAt(x: number, coins: Coin[], seat: 1 | 2 | 3 | 4 = 1): boolean {
  const baseline = (seat === 1 || seat === 3) ? 0.86 : 0.14;
  const minDist = STRIKER_R + COIN_R + 0.001;
  const minDistSq = minDist * minDist;
  for (const c of coins) {
    if (c.sunk) continue;
    const dx = c.x - x;
    const dy = c.y - baseline;
    if (dx * dx + dy * dy < minDistSq) return false;
  }
  return true;
}

function powerColor(power: number, alpha = 1): string {
  const hue = Math.round(130 - power * 130);
  return `hsla(${hue}, 92%, 55%, ${alpha})`;
}

export function initialLayout(): Coin[] {
  const coins: Coin[] = [];
  coins.push({ id: 0, color: 'queen', x: 0.5, y: 0.5, sunk: false });

  const innerR = COIN_R * 2;
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 3) * i;
    coins.push({
      id: 1 + i,
      color: i % 2 === 0 ? 'white' : 'black',
      x: 0.5 + Math.cos(a) * innerR,
      y: 0.5 + Math.sin(a) * innerR,
      sunk: false,
    });
  }

  const outerR = COIN_R * 4;
  for (let i = 0; i < 12; i++) {
    const a = (Math.PI / 6) * i + Math.PI / 12;
    coins.push({
      id: 7 + i,
      color: i % 2 === 0 ? 'black' : 'white',
      x: 0.5 + Math.cos(a) * outerR,
      y: 0.5 + Math.sin(a) * outerR,
      sunk: false,
    });
  }
  return coins;
}

export const STRIKER_HOME = (_seat: Seat): Striker => ({
  x: 0.5,
  y: BASELINE_Y,
  sunk: false,
});
